import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiDetailFields,
  MiList,
  MiLoader,
  MiMenu,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ORDER_STATUS_DISPLAY,
  ORDER_STATUS_TITLE_COLORS,
  PLATFORM_MODULES,
  WORK_ORDER_COLORS_MAP,
  WORK_ORDER_STATUSES_LABELS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import {
  formatDate,
  getHomeOrderCardStyles,
  getStyledFieldSet,
  getWorkOrderStatus,
} from "@miplatform/utils";
import {
  mdiFileDocumentOutline,
  mdiFormatListBulleted,
  mdiRefresh,
} from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";
import { OrderAlerts } from "../../Orders/OrdersList/OrderAlerts/OrderAlerts";
import { OrderInfoPopper } from "../../Orders/OrdersList/OrderInfoPopper/OrderInfoPopper";
import { ORDERS_LIST_CONFIG } from "../../Orders/OrdersList/orderListConfig";
import { OrdersProvider } from "../../Orders/OrdersProvider";
import { orderHoverCardConfig } from "../OrdersTab/orderCardConfig";
import { dashboardDetailSet } from "./homeDashboardDetails";

const CardListSection = ({
  data,
  home,
  isLoading,
  title,
  setSelectedOrder,
  handleOrderActivityDrawerOpen,
  emptyListText,
}) => {
  const { classes, theme } = useStyles();
  const navigate = useNavigate();

  return (
    <Box className={classes.sectionContainer}>
      {isLoading ? (
        <MiLoader isLoading={isLoading} style={{ height: "100%" }} />
      ) : (
        <>
          <Box className={classes.listSectionHeader}>
            <Typography fontWeight={theme.fontWeight.semiBold}>
              {title}
            </Typography>
            <MiMenu
              data={[
                {
                  label: "View All Orders",
                  icon: mdiFormatListBulleted,
                  color: theme.palette.grey.main,
                  action: () => {
                    navigate(`/homes/${home.id}/orders`);
                  },
                },
              ]}
            />
          </Box>
          <Box className={classes.listContainer}>
            <MiList
              viewType="card"
              data={data}
              cardProps={{
                isAlert: ORDERS_LIST_CONFIG.All?.isAlert,
                renderTitle: (item) => {
                  const status = item.isWorkOrder
                    ? getWorkOrderStatus(item)
                    : item.status;
                  const statusLabel = item.isWorkOrder
                    ? WORK_ORDER_STATUSES_LABELS[status]
                    : ORDER_STATUS_DISPLAY[status];
                  const color = item.isWorkOrder
                    ? WORK_ORDER_COLORS_MAP[status]?.drawerStatus
                    : ORDER_STATUS_TITLE_COLORS[status];

                  return (
                    <Box display="flex" alignItems="center">
                      <Box display="flex" flexDirection="column" width={80}>
                        <Typography
                          fontSize={theme.fontSize.small}
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          color={theme.palette.grey.main}
                        >
                          Status
                        </Typography>
                        <Typography
                          fontSize={theme.fontSize.medium}
                          fontWeight={theme.fontWeight.medium}
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          color={color}
                        >
                          {statusLabel}
                        </Typography>
                      </Box>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          mx: theme.spacing(2),
                        }}
                      />
                      <Box display="flex" flexDirection="column">
                        <Box
                          display="flex"
                          gap={theme.spacing(1)}
                          alignItems="center"
                        >
                          <Typography
                            color="primary.main"
                            fontWeight={theme.fontWeight.medium}
                            fontSize={theme.fontSize.medium}
                          >
                            {item.isWorkOrder
                              ? `${item.assignment?.assignmentTemplateName} #${item.attemptNumber}`
                              : item.orderTemplateName}
                          </Typography>
                          {!item.isWorkOrder && (
                            <OrderInfoPopper
                              order={item}
                              fieldsConfig={orderHoverCardConfig}
                            />
                          )}
                          {ORDERS_LIST_CONFIG.All?.getAlerts && (
                            <OrderAlerts
                              alerts={ORDERS_LIST_CONFIG.All?.getAlerts(item)}
                            />
                          )}
                        </Box>
                        <Typography
                          fontSize={theme.fontSize.small}
                          width={180}
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          color={theme.palette.grey.main}
                        >
                          {item.isWorkOrder
                            ? item.assignedUserName || "--"
                            : item.orderTemplateTradeName || "--"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                },
                renderItemActions: (item) => {
                  return (
                    <MiMenu
                      data={[
                        {
                          label: "Details",
                          icon: mdiFormatListBulleted,
                          color: theme.palette.grey.main,
                          action: () => {
                            setSelectedOrder(item);
                            if (!item.isWorkOrder) {
                              handleOrderActivityDrawerOpen();
                            }
                          },
                        },
                      ]}
                    />
                  );
                },
              }}
              loading={isLoading}
              emptyListProps={{
                icon: mdiFileDocumentOutline,
                text: emptyListText || "No Orders found",
              }}
              hideListPagination
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const DetailsSection = ({ home, isLoading }) => {
  const { classes, theme } = useStyles();
  const navigate = useNavigate();
  const edit = useEdit({ ...home, cityId: home.cityId || "" });
  const fieldSet = dashboardDetailSet(edit);

  return (
    <Box className={classes.sectionContainer}>
      {isLoading ? (
        <MiLoader isLoading={isLoading} style={{ height: "100%" }} />
      ) : (
        <>
          <Box className={classes.listSectionHeader} mb={theme.spacing(3)}>
            <Typography fontWeight={theme.fontWeight.semiBold}>
              Home Details
            </Typography>
            <MiMenu
              data={[
                {
                  label: "View All Details",
                  icon: mdiFormatListBulleted,
                  color: theme.palette.grey.main,
                  action: () => {
                    navigate(`/homes/${home.id}/details`);
                  },
                },
              ]}
            />
          </Box>
          <Box className={classes.listContainer} pt={1}>
            <MiDetailFields
              detailFields={getStyledFieldSet(fieldSet)}
              edit={edit}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export const HomeDashboardTab = ({
  home = {},
  orders = [],
  attempts,
  fetchAll,
  setSelectedOrder,
  setSelectedWorkOrder,
  handleOrderActivityDrawerOpen,
  lastFetchedTimestamp,
  isLoading,
}) => {
  const { classes, theme } = useStyles();

  const ordersByModule = Object.groupBy(
    orders,
    (order) => order.orderTemplateModule
  );

  const workOrders = attempts
    .map((attempt) => {
      return {
        ...attempt,
        isWorkOrder: true,
        containerStyles: getHomeOrderCardStyles(
          theme,
          attempt?.assignment?.workOrder?.status
        ),
      };
    })
    .sort((a, b) =>
      a.assignment?.workOrder?.workOrderTemplateName?.localeCompare(
        b.assignment?.workOrder?.workOrderTemplateName
      )
    );

  const getStylesOrderCards = (orders = []) => {
    return orders.map((order) => {
      return {
        ...order,
        isWorkOrder: false,
        containerStyles: getHomeOrderCardStyles(theme, order?.orderStatus),
      };
    });
  };

  return (
    <OrdersProvider>
      <MiBox className={classes.root}>
        <MiBuildPageHeader
          title="Dashboard"
          titleFontSize={theme.fontSize.xlarge}
          leftComponent={
            <Typography
              sx={{
                margin: theme.spacing(0.5, 0, 0, 1),
              }}
              fontSize={theme.fontSize.small}
              color={theme.palette.grey.darkerAccent}
            >
              {lastFetchedTimestamp && !isLoading
                ? `Last Updated: ${formatDate(
                    lastFetchedTimestamp,
                    "MM/DD/YY @ hh:mm A"
                  )}`
                : ""}
            </Typography>
          }
          hasDivider={false}
          style={{
            backgroundColor: theme.palette.grey.mediumLight,
          }}
          actionComponent={
            <MiButton
              title="Refresh"
              variant={BUTTON_VARIANTS.OUTLINED}
              startIcon={{
                path: mdiRefresh,
                iconSize: 0.7,
              }}
              data-testid="refresh-home-dashboard-btn"
              onClick={() => fetchAll()}
            />
          }
        />
        <Box className={classes.gridContainer}>
          <Box className={classes.gridItem}>
            <CardListSection
              title="NHC Orders"
              data={getStylesOrderCards(
                ordersByModule[PLATFORM_MODULES.MI_SCHEDULING] || []
              )}
              home={home}
              isLoading={isLoading}
              setSelectedOrder={setSelectedOrder}
              handleOrderActivityDrawerOpen={handleOrderActivityDrawerOpen}
              emptyListText="No NHC Orders found"
            />
          </Box>

          <Box className={classes.gridItem}>
            <CardListSection
              title="NHC Work Orders"
              data={workOrders || []}
              home={home}
              isLoading={isLoading}
              setSelectedOrder={setSelectedWorkOrder}
              handleOrderActivityDrawerOpen={handleOrderActivityDrawerOpen}
              emptyListText="No NHC Work Orders found"
            />
          </Box>

          <Box className={classes.gridItem}>
            <CardListSection
              title="Dispatch Orders"
              data={getStylesOrderCards(
                ordersByModule[PLATFORM_MODULES.MI_DISPATCH] || []
              )}
              home={home}
              isLoading={isLoading}
              setSelectedOrder={setSelectedOrder}
              handleOrderActivityDrawerOpen={handleOrderActivityDrawerOpen}
              emptyListText="No Dispatch Orders found"
            />
          </Box>

          <Box className={classes.gridItem}>
            <DetailsSection home={home} isLoading={isLoading} />
          </Box>
        </Box>
      </MiBox>
    </OrdersProvider>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "inherit",
    padding: 0,
    margin: 0,
    boxShadow: "none",
    backgroundColor: theme.palette.grey.mediumLight,
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 2, 2),
    height: "100%",
    minHeight: 0,
  },
  gridItem: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey.accent}`,
    minHeight: 0,
  },
  listSectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    background: theme.palette.grey.light,
    borderBottom: `1px solid ${theme.palette.grey.lightAccent}`,
    padding: theme.spacing(2.5, 2, 2, 2),
    flexShrink: 0,
  },
  sectionContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  listContainer: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
  },
}));
